/* eslint-disable react-refresh/only-export-components */
import { createContext, useCallback, useContext, useMemo, useState, type ReactNode } from 'react';
import { CheckCircle2 } from 'lucide-react';
import { Modal, ModalContent, ModalTitle, ModalDescription } from '@/components/ui/modal';
import { LeadForm } from '@/components/sections/lead-form';
import { useLang } from '@/lib/LanguageContext';
import { ta } from '@/lib/i18n';
import { t } from '@/lib/translations';

// Phase 27.89 — modal lead global. Les CTA du site appellent openForm() pour
// ouvrir une card avec le MÊME formulaire que la section #contact. À la
// soumission → la card form se ferme + le popup succès s'affiche. Le popup
// succès est centralisé ici (partagé section + modal) ; vit au niveau racine
// (stable) → survit aux remontages des routes (query params ?utm_* nettoyés).

type LeadModalCtx = { openForm: () => void; showSuccess: () => void };
const LeadModalContext = createContext<LeadModalCtx | null>(null);

// Défaut no-op si pas de provider (jamais en prod : le provider est au niveau
// racine __root.tsx). Évite de devoir wrapper chaque test unitaire.
const NOOP_CTX: LeadModalCtx = { openForm: () => {}, showSuccess: () => {} };

export function useLeadModal(): LeadModalCtx {
  return useContext(LeadModalContext) ?? NOOP_CTX;
}

export function LeadModalProvider({ children }: { children: ReactNode }) {
  const { lang } = useLang();
  const formText = ta(t.form, lang);
  const merciText = ta(t.merci, lang);
  const [formOpen, setFormOpen] = useState(false);
  const [successOpen, setSuccessOpen] = useState(false);

  const openForm = useCallback(() => setFormOpen(true), []);
  const showSuccess = useCallback(() => {
    // Ferme d'abord la card form, PUIS ouvre le succès après un court délai :
    // Radix gère mal 2 Dialog qui transitionnent dans le même tick (le 2e se
    // referme aussitôt — focus/pointer-events lock). 250ms = card form démontée.
    setFormOpen(false);
    window.setTimeout(() => setSuccessOpen(true), 250);
  }, []);

  const value = useMemo(() => ({ openForm, showSuccess }), [openForm, showSuccess]);

  return (
    <LeadModalContext.Provider value={value}>
      {children}

      {/* Card formulaire ouverte par les CTA */}
      <Modal open={formOpen} onOpenChange={setFormOpen}>
        <ModalContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <ModalTitle className="mb-1 font-display text-2xl text-foreground">
            {formText.title}
          </ModalTitle>
          <ModalDescription className="mb-5 font-sans text-sm leading-relaxed text-muted-foreground">
            {formText.guarantee_body}
          </ModalDescription>
          <LeadForm onSuccess={showSuccess} />
        </ModalContent>
      </Modal>

      {/* Popup succès — partagé (section + modal) */}
      <Modal open={successOpen} onOpenChange={setSuccessOpen}>
        <ModalContent className="text-center">
          <CheckCircle2 className="mx-auto mb-2 h-14 w-14 text-secondary" aria-hidden="true" />
          <span className="block font-display text-sm italic tracking-wide text-secondary">
            {merciText.eyebrow}
          </span>
          <ModalTitle className="mb-2 font-display text-2xl leading-[1.15] text-balance text-foreground md:text-3xl">
            {merciText.title}
          </ModalTitle>
          <ModalDescription className="font-sans text-base leading-relaxed text-pretty hyphens-auto text-muted-foreground">
            {merciText.body}
          </ModalDescription>
        </ModalContent>
      </Modal>
    </LeadModalContext.Provider>
  );
}
